import { useState, useEffect, useCallback } from 'react'
import emailjs from '@emailjs/browser'
import styles from './App.module.css'

// Initialize EmailJS
emailjs.init("evzCTi6YyPyEFUJOm")

function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    instagram: '',
    message: '',
    preferredDate: '',
    size: '',
    placement: ''
  })

  // Add portfolio state
  const [currentSet, setCurrentSet] = useState(0)
  const [slideDirection, setSlideDirection] = useState(null)
  
  // Add preview state and handlers
  const [previewImage, setPreviewImage] = useState(null)
  const [_currentImageIndex, setCurrentImageIndex] = useState(0)
  
  // Portfolio image sets
  const portfolioSets = [
    // First set (original images)
    [
      '/portfolio/tattoo-1.webp',
      '/portfolio/tattoo-2.webp',
      '/portfolio/tattoo-3.webp',
      '/portfolio/tattoo-4.webp',
      '/portfolio/tattoo-5.webp',
      '/portfolio/tattoo-6.webp',
    ],
    // Second set
    [
      '/portfolio/tattoo-7.webp',
      '/portfolio/tattoo-8.webp',
      '/portfolio/tattoo-9.webp',
      '/portfolio/tattoo-10.webp',
      '/portfolio/tattoo-11.webp',
      '/portfolio/tattoo-12.webp',
    ],
  ]

  // Flatten portfolio images for easier navigation
  const allImages = portfolioSets.flat()

  const [hasScrolled, setHasScrolled] = useState(false)
  const [showScrollIndicator, setShowScrollIndicator] = useState(true)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [retryCount, setRetryCount] = useState(0)
  const [lastSubmissionData, setLastSubmissionData] = useState(null)

  // File upload state temporarily disabled
  /*
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };
  */

  const handleNextSet = () => {
    setSlideDirection('next')
    setCurrentSet((prev) => (prev + 1) % portfolioSets.length)
  }

  const handlePrevSet = () => {
    setSlideDirection('prev')
    setCurrentSet(prev => prev - 1)
  }

  const openPreview = (setIndex, imageIndex) => {
    const flatIndex = setIndex * 6 + imageIndex
    setCurrentImageIndex(flatIndex)
    setPreviewImage(allImages[flatIndex])
  }

  const closePreview = useCallback(() => {
    setPreviewImage(null)
  }, [])

  const showNextImage = useCallback(() => {
    setCurrentImageIndex((prev) => {
      const next = (prev + 1) % allImages.length
      setPreviewImage(allImages[next])
      return next
    })
  }, [allImages])

  const showPrevImage = useCallback(() => {
    setCurrentImageIndex((prev) => {
      const next = prev === 0 ? allImages.length - 1 : prev - 1
      setPreviewImage(allImages[next])
      return next
    })
  }, [allImages])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!previewImage) return
      
      switch (e.key) {
        case 'ArrowRight':
          showNextImage()
          break
        case 'ArrowLeft':
          showPrevImage()
          break
        case 'Escape':
          closePreview()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [previewImage, showNextImage, showPrevImage, closePreview])

  // Enhanced form submission with reliability features
  const saveToLocalStorage = (submissionData) => {
    const backup = {
      data: submissionData,
      timestamp: new Date().toISOString(),
      id: Date.now().toString()
    };
    
    const existingBackups = JSON.parse(localStorage.getItem('booking_backups') || '[]');
    existingBackups.push(backup);
    
    // Keep only last 10 backups
    if (existingBackups.length > 10) {
      existingBackups.splice(0, existingBackups.length - 10);
    }
    
    localStorage.setItem('booking_backups', JSON.stringify(existingBackups));
    return backup.id;
  };

  const removeFromLocalStorage = (backupId) => {
    const existingBackups = JSON.parse(localStorage.getItem('booking_backups') || '[]');
    const filteredBackups = existingBackups.filter(backup => backup.id !== backupId);
    localStorage.setItem('booking_backups', JSON.stringify(filteredBackups));
  };

  const sendStudioEmail = async (submissionData, attempt = 1) => {
    const maxRetries = 3;
    
    const templateParams = {
      name: submissionData.name || 'Not provided',
      email: submissionData.email || 'Not provided',
      message: submissionData.message || 'Not provided',
      time: new Date().toLocaleString(),
      from_name: submissionData.name || 'Not provided',
      from_email: submissionData.email || 'Not provided',
      phone: submissionData.phone || 'Not provided', 
      instagram: submissionData.instagram || 'Not provided',
      size: submissionData.size || 'Not provided',
      placement: submissionData.placement || 'Not provided',
      preferred_date: submissionData.preferredDate || 'Not provided',
      submitted_at: submissionData.submittedAt || new Date().toISOString(),
      contact_info: `Email: ${submissionData.email}\nPhone: ${submissionData.phone}${submissionData.instagram ? `\nInstagram: ${submissionData.instagram}` : ''}`,
      tattoo_details: `Size: ${submissionData.size}\nPlacement: ${submissionData.placement}\nAvailability: ${submissionData.preferredDate}`,
      full_message: `${submissionData.message}\n\n--- Booking Details ---\nName: ${submissionData.name}\nEmail: ${submissionData.email}\nPhone: ${submissionData.phone}\nInstagram: ${submissionData.instagram || 'Not provided'}\nSize: ${submissionData.size}\nPlacement: ${submissionData.placement}\nAvailability: ${submissionData.preferredDate}\nSubmitted: ${submissionData.submittedAt}`
    };
    
    try {
      const response = await emailjs.send(
        'service_j95v92f',
        'template_dwir8mk',
        templateParams,
        { publicKey: "evzCTi6YyPyEFUJOm" }
      );
      return { success: true, response };
    } catch (error) {
      if (error.message?.includes('authentication failed') || error.message?.includes('535')) {
        return { success: false, error: error.message, isAuthError: true };
      }
      
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
        await new Promise(resolve => setTimeout(resolve, delay));
        return sendStudioEmail(submissionData, attempt + 1);
      }
      
      return { success: false, error: error.message || 'Studio email failed after retries' };
    }
  };

  const sendCustomerEmail = async (submissionData, attempt = 1) => {
    const maxRetries = 3;
    
    const customerTemplateParams = {
      email: submissionData.email,
      from_name: submissionData.name || 'Not provided',
      size: submissionData.size || 'Not provided',
      placement: submissionData.placement || 'Not provided',
      preferred_date: submissionData.preferredDate || 'Not provided',
      submitted_at: submissionData.submittedAt || new Date().toLocaleString(),
      message: submissionData.message || 'Not provided'
    };
    
    try {
      const response = await emailjs.send(
        'service_j95v92f',
        'template_ytrz134',
        customerTemplateParams,
        { publicKey: "evzCTi6YyPyEFUJOm" }
      );
      return { success: true, response };
    } catch (error) {
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
        await new Promise(resolve => setTimeout(resolve, delay));
        return sendCustomerEmail(submissionData, attempt + 1);
      }
      
      return { success: false, error: error.message || 'Customer email failed after retries' };
    }
  };

  const attemptEmailSend = async (_, submissionData) => {
    // FIRST: Send studio email with retry logic
    const studioResult = await sendStudioEmail(submissionData);
    
    // If studio email fails, do NOT send customer confirmation
    if (!studioResult.success) {
      return {
        success: false,
        error: studioResult.error,
        isAuthError: studioResult.isAuthError
      };
    }
    
    // ONLY send customer confirmation AFTER studio email is successful
    const customerResult = await sendCustomerEmail(submissionData);
    
    return {
      success: true,
      studioResponse: studioResult.response,
      confirmationResponse: customerResult.success ? customerResult.response : null,
      confirmationError: customerResult.success ? null : customerResult.error,
      bothEmailsSent: customerResult.success
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setRetryCount(0);

    // Create submission data object
    const submissionData = {
      ...formData,
      submittedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Save to localStorage as backup immediately
    const backupId = saveToLocalStorage(submissionData);
    setLastSubmissionData({ ...submissionData, backupId });

    try {
      const result = await attemptEmailSend(null, submissionData);
      
      if (result.success) {
        // Set different success status based on whether both emails were sent
        if (result.bothEmailsSent) {
          setSubmitStatus('success-both');
        } else {
          setSubmitStatus('success-partial');
        }
        

        
        // Remove from localStorage since it was successful
        removeFromLocalStorage(backupId);
        
        // Clear form
        setFormData({
          name: '',
          email: '',
          phone: '',
          instagram: '',
          message: '',
          preferredDate: '',
          size: '',
          placement: ''
        });
        
        setRetryCount(0);
        setLastSubmissionData(null);
        
      } else {
        setSubmitStatus('error');
        setRetryCount(result.attempts || 1);
        
        // Keep the backup in localStorage for manual review
      }
      
    } catch (error) {
      setSubmitStatus('error');
      setRetryCount(1);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manual retry function for failed submissions
  const retryLastSubmission = async () => {
    if (!lastSubmissionData) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Use the same improved email sending method for retry
      const result = await attemptEmailSend(null, lastSubmissionData);
      
      if (result.success) {
        // Set different success status based on whether both emails were sent
        if (result.bothEmailsSent) {
          setSubmitStatus('success-both');
        } else {
          setSubmitStatus('success-partial');
        }
        removeFromLocalStorage(lastSubmissionData.backupId);
        setLastSubmissionData(null);
        setRetryCount(0);
      } else {
        setSubmitStatus('error');
        setRetryCount(result.attempts || 1);
      }
      
    } catch (error) {

      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  function scrollToPortfolio() {
    setShowScrollIndicator(false)
    setHasScrolled(true)
    const portfolioSection = document.getElementById('portfolio')
    portfolioSection.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setHasScrolled(true)
        setShowScrollIndicator(false)
      } else {
        setHasScrolled(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  function scrollToSection(sectionId) {
    const section = document.getElementById(sectionId)
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' })
    }
  }

  function scrollToTop(e) {
    e.preventDefault()
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const createSparkle = (e) => {
    // Don't create sparkles if clicking on interactive elements
    if (
      e.target.closest('button') ||
      e.target.closest('a') ||
      e.target.closest('input') ||
      e.target.closest('textarea') ||
      e.target.closest('.' + styles.portfolioItem) ||
      e.target.closest('.' + styles.modalNavButton) ||
      e.target.closest('.' + styles.closeButton)
    ) {
      return;
    }

    const sparkles = ['✨', '⭐️', '🌟', '💫'];
    const sparkleContainer = document.createElement('div');
    const numSparkles = Math.floor(Math.random() * 2) + 3; // 3-4 sparkles

    // Create sparkles in a container for better performance
    for (let i = 0; i < numSparkles; i++) {
      const sparkle = document.createElement('div');
      sparkle.className = styles.sparkle;
      sparkle.textContent = sparkles[Math.floor(Math.random() * sparkles.length)];
      
      // Add slight random offset for each sparkle
      const offsetX = i === 0 ? 0 : (Math.random() * 60 - 30);
      const offsetY = i === 0 ? 0 : (Math.random() * 60 - 30);
      const delay = i * 50; // Stagger the animations
      
      sparkle.style.left = (e.clientX + offsetX) + 'px';
      sparkle.style.top = (e.clientY + offsetY) + 'px';
      sparkle.style.animationDelay = delay + 'ms';
      
      sparkleContainer.appendChild(sparkle);
    }

    document.body.appendChild(sparkleContainer);

    // Clean up all sparkles after the longest animation + delay
    setTimeout(() => {
      if (sparkleContainer && document.body.contains(sparkleContainer)) {
        sparkleContainer.remove();
      }
    }, 1500); // Increased to account for delays
  };

  // Debounce the sparkle creation to prevent too many sparkles
  const debouncedCreateSparkle = useCallback((e) => {
    if (window.lastSparkleTime && Date.now() - window.lastSparkleTime < 50) {
      return; // Prevent creating sparkles too quickly
    }
    window.lastSparkleTime = Date.now();
    createSparkle(e);
  }, []);

  useEffect(() => {
    document.addEventListener('click', debouncedCreateSparkle);
    return () => document.removeEventListener('click', debouncedCreateSparkle);
  }, [debouncedCreateSparkle]);

  const ImagePreviewModal = ({ image, onClose, onNext, onPrev }) => {
    // Handle background click
    const handleBackgroundClick = (e) => {
      // Close if clicking the modal background (the dark overlay)
      if (e.target.className === styles.imagePreviewModal) {
        onClose();
      }
    };

    // Add/remove body scroll lock
    useEffect(() => {
      const scrollY = window.scrollY;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
      body.style.left = '0';
      
      return () => {
        body.style.position = '';
        body.style.top = '';
        body.style.width = '';
        body.style.left = '';
        window.scrollTo(0, parseInt(scrollY || '0'));
      };
    }, []);

    if (!image) return null;

    return (
      <div 
        className={styles.imagePreviewModal}
        onClick={handleBackgroundClick}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.modalContent}>
          <div className={styles.modalImageContainer}>
            <img 
              src={image} 
              alt="Preview" 
              className={styles.modalImage}
              loading="eager"
            />
          </div>
          <button 
            className={`${styles.modalNavButton} ${styles.modalPrevButton}`}
            onClick={onPrev}
            aria-label="Previous image"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M14.6 18l1.4-1.4L11.4 12l4.6-4.6L14.6 6l-6 6z"/>
            </svg>
          </button>
          <button 
            className={`${styles.modalNavButton} ${styles.modalNextButton}`}
            onClick={onNext}
            aria-label="Next image"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M9.4 18L8 16.6l4.6-4.6L8 7.4 9.4 6l6 6z"/>
            </svg>
          </button>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Close preview"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {/* Navigation Bar */}
      <nav className={styles.navbar}>
        <a href="/" className={styles.navBrand} onClick={scrollToTop}>
          <img 
            src="/smallseyss-logo.svg" 
            className={styles.logoIcon} 
            alt="Smallseyss Logo"
            data-scrolled={hasScrolled}
          />
        </a>
        <div className={styles.navLinks}>
          <a href="/" className={styles.navLink} onClick={scrollToTop}>🧚✨</a>
          <a 
            href="#portfolio" 
            className={styles.navLink} 
            onClick={(e) => {
              e.preventDefault()
              scrollToSection('portfolio')
            }}
          >
            portfolio
          </a>
          <a 
            href="#booking" 
            className={styles.bookNowButton}
            onClick={(e) => {
              e.preventDefault()
              scrollToSection('booking')
            }}
          >
            book now
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroImage}>
          <img 
            src="/hero-image.webp" 
            alt="Tattoo Art" 
            loading="eager"
            width="1920"
            height="1080"
            sizes="100vw"
          />
        </div>
        <img 
          src="/smallseyss-logo.svg" 
          className={styles.heroLogo} 
          alt="Smallseyss Logo"
          data-scrolled={hasScrolled}
        />
        <div className={styles.heroContent}>
          <p className={styles.heroGreeting}>hey, i'm</p>
          <h1 className={styles.heroTitle}>Anna Seyss</h1>
          <p className={styles.heroSubtitle}>smallseyss.ink</p>
          <p className={styles.heroDescription}>
            creating tiny handwritten letterings and fine line tattoos with love in Berlin ✨
          </p>
          <div className={styles.heroButtons}>
            <a href="https://instagram.com/smallseyss" target="_blank" rel="noopener noreferrer" className={styles.heroButtonDark}>
              <svg className={styles.instagramIcon} viewBox="0 0 24 24">
                <path fill="currentColor" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.012-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a 
              href="#booking" 
              className={styles.heroButton}
              onClick={(e) => {
                e.preventDefault()
                scrollToSection('booking')
              }}
            >
              book now
              <svg className={styles.arrowIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L20 12L12 20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>
      </section>

      {showScrollIndicator && (
        <div className={styles.scrollIndicatorContainer}>
          <div className={styles.scrollIndicator} onClick={scrollToPortfolio} aria-label="Scroll to portfolio">
            <div className={styles.arrow}></div>
          </div>
        </div>
      )}

      {/* Portfolio Section */}
      <section id="portfolio" className={styles.portfolioSection}>
        <div className={styles.portfolioContainer}>
          <h2 className={styles.portfolioTitle}>Portfolio</h2>
          <div className={styles.portfolioGridContainer}>
            <div className={styles.portfolioGridWrapper}>
              {portfolioSets.map((set, setIndex) => (
                <div 
                  key={setIndex} 
                  className={styles.portfolioGridSet}
                  data-active={setIndex === currentSet}
                  data-direction={slideDirection || (setIndex === 0 ? null : 'prev')}
                >
                  {set.map((src, index) => (
                    <div 
                      key={`${setIndex}-${index}`} 
                      className={styles.portfolioItem}
                      onClick={() => openPreview(setIndex, index)}
                    >
                      <img 
                        src={src}
                        alt={`Tattoo work ${setIndex * 6 + index + 1}`}
                        loading={setIndex === 0 && index < 3 ? "eager" : "lazy"}
                        width="400"
                        height="400"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {currentSet > 0 && (
              <div 
                className={styles.portfolioPrevIndicator}
                onClick={handlePrevSet}
              >
                <svg viewBox="0 0 24 24" width="24" height="24" className={styles.arrowIcon}>
                  <path fill="currentColor" d="M14.6 18l1.4-1.4L11.4 12l4.6-4.6L14.6 6l-6 6z"/>
                </svg>
              </div>
            )}
            {currentSet < portfolioSets.length - 1 && (
              <div 
                className={styles.portfolioNextIndicator}
                onClick={handleNextSet}
              >
                <svg viewBox="0 0 24 24" width="24" height="24" className={styles.arrowIcon}>
                  <path fill="currentColor" d="M9.4 18L8 16.6l4.6-4.6L8 7.4 9.4 6l6 6z"/>
                </svg>
              </div>
            )}
          </div>

          {/* Image Preview Modal */}
          {previewImage && (
            <ImagePreviewModal
              image={previewImage}
              onClose={closePreview}
              onNext={showNextImage}
              onPrev={showPrevImage}
            />
          )}

          <div className={styles.portfolioActions}>
            <a 
              href="https://instagram.com/smallseyss" 
              target="_blank" 
              rel="noopener noreferrer" 
              className={styles.viewMoreButton}
            >
              view more on instagram
              <svg className={styles.instagramIcon} viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.012-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
        </a>
      </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section id="booking" className={styles.bookingSection}>
        <div className={styles.bookingContainer}>
          <h2 className={styles.bookingTitle}>Book Your Appointment</h2>
          <p className={styles.bookingSubtitle}>
            Fill out the form below to request a tattoo appointment with me 🧚✨<br />
            <i>Du kannst es auch auf Deutsch ausfüllen</i> ☺️
          </p>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.label}>Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={styles.input}
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="phone" className={styles.label}>Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="+49 123 456 7890"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="instagram" className={styles.label}>Instagram Handle (optional)</label>
                <input
                  type="text"
                  id="instagram"
                  name="instagram"
                  placeholder="@yourusername"
                  value={formData.instagram}
                  onChange={handleChange}
                  className={styles.input}
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="size" className={styles.label}>Size</label>
                <select
                  id="size"
                  name="size"
                  value={formData.size}
                  onChange={handleChange}
                  required
                  className={styles.input}
                >
                  <option value="" disabled>Select size</option>
                  <option value="tiny">tiny (1-3cm)</option>
                  <option value="small">small (3-5cm)</option>
                  <option value="medium">medium (5-10cm)</option>
                  <option value="large">large (10cm+)</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="placement" className={styles.label}>Placement</label>
                <select
                  id="placement"
                  name="placement"
                  value={formData.placement}
                  onChange={handleChange}
                  required
                  className={styles.input}
                >
                  <option value="" disabled>Select placement</option>
                  <option value="arm">arm</option>
                  <option value="leg">leg</option>
                  <option value="torso">chest</option>
                  <option value="back">back</option>
                  <option value="hand">hand</option>
                  <option value="other">other</option>
                </select>
              </div>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="preferredDate" className={styles.label}>Availability for your appointment</label>
              <input
                type="text"
                id="preferredDate"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                placeholder="e.g., after 4pm any day next week, between 10am and 6pm any day next week, flexible"
                required
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="message" className={styles.label}>Tattoo Description</label>
              <textarea
                id="message"
                name="message"
                placeholder="Please describe your tattoo idea in detail. Please include size and style preferences and send reference images to booking@smallseyss.ink if you want to :)"
                value={formData.message}
                onChange={handleChange}
                required
                className={styles.textarea}
                rows={6}
              />
            </div>
            {/* File upload field temporarily disabled
            <div className={styles.formGroup}>
              <label htmlFor="files" className={styles.label}>Reference Images (optional)</label>
              <input
                type="file"
                id="files"
                name="files"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className={styles.fileInput}
              />
              {selectedFiles.length > 0 && (
                <div className={styles.fileList}>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className={styles.fileItem}>
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            */}
            <p className={styles.formNote}>
              You can email reference images to booking@smallseyss.ink with your name in the subject line if you want to.
            </p>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Submit Booking Request'}
        </button>

            {/* Enhanced status messages */}
            {(submitStatus === 'success-both' || submitStatus === 'success-partial') && (
              <div className={styles.successMessage}>
                <h3>🎉 Booking Request Sent Successfully!</h3>
                <p>Thank you! Your booking request has been sent successfully. I'll get back to you within 24-48 hours.</p>
                
                {submitStatus === 'success-both' && (
                  <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '10px', borderRadius: '5px', margin: '10px 0', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                    <p style={{ margin: 0, color: '#10B981', fontWeight: 'bold' }}>
                      ✅ Confirmation email sent to your inbox!
                    </p>
                  </div>
                )}
                
                {submitStatus === 'success-partial' && (
                  <div style={{ background: 'rgba(251, 191, 36, 0.1)', padding: '10px', borderRadius: '5px', margin: '10px 0', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
                    <p style={{ margin: 0, color: '#F59E0B', fontWeight: 'bold' }}>
                      ⚠️ Your request was received, but confirmation email may be delayed
                    </p>
                  </div>
                )}
                
                <p><strong>What's next?</strong></p>
                <ul>
                  <li>Check your email for a confirmation (including spam folder)</li>
                  <li>I'll review your request and get back to you soon</li>
                  <li>Feel free to follow <a href="https://instagram.com/smallseyss" target="_blank" rel="noopener noreferrer">@smallseyss</a> for updates</li>
                  <li><strong>If you don't hear back within 2 days:</strong> Send me a DM on <a href="https://instagram.com/smallseyss" target="_blank" rel="noopener noreferrer">Instagram @smallseyss</a> to confirm your request</li>
                </ul>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className={styles.errorMessage}>
                <h3>⚠️ Submission Failed</h3>
                <p>Sorry, there was an error sending your booking request after {retryCount} attempt{retryCount > 1 ? 's' : ''}.</p>
                
                {/* Check if it's an authentication error */}
                {lastSubmissionData && 
                 typeof window !== 'undefined' && 
                 window.console && 
                 console.error.toString().includes('authentication') && (
                  <div className={styles.authErrorDetails}>
                    <p><strong>🔐 Email service authentication issue detected.</strong></p>
                    <p>This is a temporary technical issue on our end. Your booking request has been safely stored.</p>
                  </div>
                )}
                
                <div className={styles.errorActions}>
                  {lastSubmissionData && (
                    <button 
                      onClick={retryLastSubmission}
                      className={styles.retryButton}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Retrying...' : 'Try Again'}
                    </button>
                  )}
                  
                  <div className={styles.alternativeContact}>
                    <p><strong>Alternative contact methods:</strong></p>
                    <ul>
                      <li>Email directly: <a href="mailto:booking@smallseyss.ink">booking@smallseyss.ink</a></li>
                      <li>Instagram DM: <a href="https://instagram.com/smallseyss" target="_blank" rel="noopener noreferrer">@smallseyss</a> (quickest response)</li>
                    </ul>
                    <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '10px', borderRadius: '5px', margin: '10px 0', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                      <p style={{ margin: 0, color: '#3B82F6', fontSize: '14px' }}>
                        💡 <strong>Tip:</strong> If you don't hear back within 2 days, send me a DM on Instagram to confirm your booking request was received.
                      </p>
                    </div>
                    {lastSubmissionData && (
                      <p className={styles.backupNote}>
                        💾 Your form data has been saved locally as backup. Copy it manually if needed.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerLinks}>
            <a href="/impressum" className={styles.footerLink}>Impressum</a>
            <a href="/datenschutz" className={styles.footerLink}>Datenschutz</a>
          </div>
          <p className={styles.copyright}>© 2024 smallseyss.ink - All rights reserved</p>
        </div>
      </footer>
      </div>
  )
}

export default App
